import {
  Box,
  Button,
  Autocomplete,
  Chip,
  TextField,
  Popover,
  IconButton,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material";
import { OrionIconButton } from "@orion/OrionComponents/OrionIconButton";
import { useState, useRef, useCallback, useEffect } from "react";

import type { Editor } from "grapesjs";

import { useAuth } from "@/contexts/AuthContext";
import {
  InlineLexicalInput,
  useInlineOffers,
} from "@/features/TextComposerV2/InlineLexicalInput";
import { SendIcon } from "@/icons";

export type TestEmailSendOptions = {
  subject?: string;
};

export type TestEmailAdapter<Scope> = {
  storageKey(scope: Scope): string;
  useHandler(scope: Scope): {
    onSend: (
      emails: string[],
      options?: TestEmailSendOptions,
      body?: string,
    ) => void | Promise<void>;
    isSending: boolean;
  };
  buttonText?: string;
  defaultSubject?: string;
  beforeSend?: (options?: TestEmailSendOptions) => Promise<boolean | void>;
};

type EmailOption = { id: string; name: string };

type TestEmailBarProps<Scope> = {
  editor?: Editor | null;
  scope: Scope;
  adapter: TestEmailAdapter<Scope>;
  buttonType?: "icon" | "button";
  buttonVariant?: "text" | "contained" | "outlined" | undefined;
  iconColor?: string;
  disabled?: boolean;
  layout?: "popover" | "inline";
  /**
   * When `layout="inline"` and `disabled`, shown instead of the form (popover still uses the trigger only).
   */
  inlineDisabledMessage?: string;
  /** Required for offer tag preview labels in the subject field. */
  siteId?: string;
};

export function TestEmailBar<Scope>({
  editor,
  scope,
  adapter,
  buttonType = "icon",
  buttonVariant = undefined,
  iconColor = "secondary.main",
  disabled = false,
  layout = "popover",
  inlineDisabledMessage = "Test send isn't available right now.",
  siteId,
}: TestEmailBarProps<Scope>) {
  const { user } = useAuth();
  const { onSend, isSending } = adapter.useHandler(scope);
  const inlineOffers = useInlineOffers(siteId ?? "");

  const getStorageKey = () => adapter.storageKey(scope);

  const getEditedBody = useCallback(() => {
    if (editor) {
      const mjmlResult = editor.runCommand("mjml-code-to-html") as {
        html: string;
        json: unknown;
        errors: unknown[];
      };
      return mjmlResult.html;
    }
    return undefined;
  }, [editor]);

  const getInitialEmails = () => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        return JSON.parse(stored) as EmailOption[];
      }
    } catch (e) {
      console.error("Failed to read emails from localStorage: ", e);
    }
    return [];
  };

  const [emailOptions, setEmailOptions] =
    useState<EmailOption[]>(getInitialEmails);
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<EmailOption[]>(getInitialEmails);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subject, setSubject] = useState(adapter.defaultSubject ?? "");
  const open = Boolean(anchorEl);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Sync subject with latest adapter value when popover opens
    setSubject(adapter.defaultSubject ?? "");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    if (layout === "popover") setAnchorEl(null);
  };

  useEffect(() => {
    if (layout !== "inline" || disabled) return;
    emailInputRef.current?.focus();
  }, [layout, disabled]);

  const handleSendTestEmail = async () => {
    // Always pass subject so validation knows user input takes precedence over basePayload
    const sendOptions = { subject };

    if (!adapter.beforeSend) {
      await onSend(
        value.map((v) => v.id),
        sendOptions,
        getEditedBody(),
      );
      handleClose();
      return;
    }

    try {
      const ok = await adapter.beforeSend(sendOptions);
      if (!ok) return;
      await onSend(
        value.map((v) => v.id),
        sendOptions,
        getEditedBody(),
      );
      handleClose();
    } catch (error) {
      console.error("Failed to send test email: ", error);
      return;
    }
  };

  const handleSendToSelf = async () => {
    // Always pass subject so validation knows user input takes precedence over basePayload
    const sendOptions = { subject };

    if (!adapter.beforeSend) {
      await onSend([user?.user.email as string], sendOptions, getEditedBody());
      handleClose();
      return;
    }

    try {
      const ok = await adapter.beforeSend(sendOptions);
      if (!ok) return;
      await onSend([user?.user.email as string], sendOptions, getEditedBody());
      handleClose();
    } catch (error) {
      console.error("Failed to send test email: ", error);
    }
  };

  const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const testSendForm = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        ...(layout === "popover" ? { marginTop: 2 } : { width: "100%" }),
      }}
    >
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        value={value}
        onChange={(_, newValue) => {
          const normalized = newValue
            .map((v): EmailOption | null =>
              typeof v === "string"
                ? { id: v, name: v }
                : v && typeof v.id === "string" && typeof v.name === "string"
                  ? v
                  : null,
            )
            .filter((v): v is EmailOption => !!v);

          const newEmails = normalized.filter(
            (v) => !emailOptions.some((opt) => opt.id === v.id),
          );
          let updatedOptions = emailOptions;
          if (newEmails.length > 0) {
            updatedOptions = [...emailOptions, ...newEmails];
            setEmailOptions(updatedOptions);
          }

          setValue(normalized);
          try {
            localStorage.setItem(getStorageKey(), JSON.stringify(normalized));
          } catch (e) {
            console.error("Failed to persist emails: ", e);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, nv) => setInputValue(nv)}
        filterOptions={(_, params) => {
          if (params.inputValue && emailRegex.test(params.inputValue)) {
            return [{ id: params.inputValue, name: params.inputValue }];
          }
          return [];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={emailInputRef}
            label="Test Email Addresses"
            placeholder="Add emails"
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                inputProps: {
                  ...params.inputProps,
                  autoComplete: "one-time-code",
                  "data-1p-ignore": true,
                  "data-bwignore": true,
                  "data-lpignore": true,
                  "data-form-type": "other",
                },
              },
            }}
          />
        )}
        renderValue={(vals, getTagProps) =>
          vals.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={typeof option === "string" ? option : option.id}
              label={typeof option === "string" ? option : option.name}
              size="small"
            />
          ))
        }
        isOptionEqualToValue={(option, v) => {
          if (typeof option === "string" || typeof v === "string")
            return option === v;
          return option.id === v.id;
        }}
      />
      <InlineLexicalInput
        label="Test Email Subject Line"
        placeholder="Enter test email subject line"
        value={subject}
        offers={inlineOffers}
        onChange={setSubject}
      />

      <Stack direction="row" spacing={3} sx={{ width: "100%" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => void handleSendTestEmail()}
          disabled={value.length === 0 || isSending}
          sx={{
            textTransform: "uppercase",
          }}
        >
          {isSending ? "Sending..." : "Send Test"}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => void handleSendToSelf()}
          disabled={isSending}
          sx={{
            textTransform: "uppercase",
          }}
        >
          {isSending ? "Sending..." : "Send To Self"}
        </Button>
      </Stack>
    </Box>
  );

  if (layout === "inline") {
    if (disabled) {
      return (
        <Box sx={{ width: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            {inlineDisabledMessage}
          </Typography>
        </Box>
      );
    }
    return testSendForm;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {buttonType === "icon" ? (
        <Tooltip
          title={adapter.buttonText ?? "Send test email"}
          placement="top"
        >
          {buttonVariant === "outlined" ? (
            <OrionIconButton
              variant="outlined"
              onClick={handleClick}
              size="small"
              disabled={disabled}
              aria-label={adapter.buttonText ?? "Send test email"}
            >
              <SendIcon fontSize="small" />
            </OrionIconButton>
          ) : (
            <span>
              <IconButton
                color="primary"
                onClick={handleClick}
                aria-label={adapter.buttonText ?? "Send test email"}
                disabled={disabled}
                sx={{
                  color: disabled ? "text.disabled" : iconColor,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          )}
        </Tooltip>
      ) : (
        <Button
          onClick={handleClick}
          startIcon={<SendIcon sx={{ fontSize: "20px" }} />}
          disabled={disabled}
          sx={{
            color: disabled ? "text.disabled" : iconColor,
          }}
          variant={buttonVariant}
        >
          {adapter.buttonText ?? "Test It"}
        </Button>
      )}

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              padding: "16px",
              minWidth: "400px",
              borderRadius: "8px",
              backgroundColor: "background.paper-elevation-2",
              marginTop: 4,
            },
          },
        }}
        TransitionProps={{
          onEntered: () => {
            emailInputRef.current?.focus();
          },
        }}
      >
        {testSendForm}
      </Popover>
    </Box>
  );
}
